import React, {useCallback, useMemo, useState} from 'react';
import _ from "lodash";
import {FormGroup} from "react-bootstrap";
import {
    useQuerySearchParams,
    Alert,
    Loading,
    ReactTable,
    PaginatedContextProvider,
    usePaginatedContext
} from "fearless-common-ui";
import {TableProps} from "./Table";
import useGet from "../hooks/useGet";
import {useNavigate} from "react-router";
import {PaginationData} from "fearless-common";


interface InnerOrderableTableProps<D extends { id: number }> extends Pick<TableProps<D>, 'memoisedColumns' | 'buildLink'>{
    pagination?: boolean;
    searchQuery?: string;
    baseEndpoint: string;
    params?: Record<string, any>;
}

export enum OrderableTableQueryParameters {
    page = 'page',
    size = 'size',
    orderBy = '_orderBy',
    orderDirection = '_orderDirection',
    query = 'query',
}

function InnerOrderableTable<D extends { id: number }>({
    baseEndpoint, params = {}, pagination = false, searchQuery = '', buildLink, memoisedColumns
}: InnerOrderableTableProps<D>) {
    const navigate = useNavigate();
    const { applyToEndpoint, extractPaginationData, pageSize, pageIndex } = usePaginatedContext();
    const [orderBy, setOrderBy] = useState<null | { key: string; direction: string }>(null);

    const fullEndpoint = useMemo(() => {
        const mockBase = 'http://mock.com';
        const url = new URL(mockBase+baseEndpoint);
        if (orderBy) {
            url.searchParams.set(OrderableTableQueryParameters.orderBy, orderBy.key);
            url.searchParams.set(OrderableTableQueryParameters.orderDirection, orderBy.direction);
        }
        if (searchQuery) {
            url.searchParams.set(OrderableTableQueryParameters.query, searchQuery);
        }
        let  modified = url.toString();
        modified = pagination ? applyToEndpoint(modified): modified;
        return modified.replace(mockBase, '');
    }, [baseEndpoint, orderBy, pagination, searchQuery, applyToEndpoint]);

    const { data: fullResp, isLoading, error } = useGet<{ data: D[], pagination?: PaginationData }>({
        endpoint: fullEndpoint,
        smoothRefresh: true,
        dataKey: '',
        // omit page and size in params to prevent duplicating parameters in
        // the URL but keep the rest.
        params: _.omit(params, [OrderableTableQueryParameters.page, OrderableTableQueryParameters.size]),
        onSuccess: extractPaginationData,
    });

    return (
        <>
            {isLoading && <Loading size="sm"/>}
            {Array.isArray(fullResp?.data) && (
                <ReactTable<D>
                    enableSorting
                    enablePagination={false}
                    paginationProps={{ pageIndex, pageSize }}
                    enableRowSelection={false}
                    enableSearching={false}
                    onDoubleClickRow={buildLink ? (record) => {
                        navigate(buildLink(record.original));
                    } : undefined}
                    // @ts-ignore
                    columns={memoisedColumns}
                    data={fullResp?.data || [] as D[]}
                    onSortChange={(sortingState) => {
                        if (sortingState.length === 0) {
                            setOrderBy(null);
                        } else {
                            const column = memoisedColumns.find(c => c.id === sortingState[0].id)
                            if (column && 'accessorKey' in column) {
                                setOrderBy({
                                    key: column.accessorKey as string,
                                    direction: sortingState[0].desc ? 'desc' : 'asc'
                                });
                            }
                        }
                    }}
                />
            )}
            {error && <Alert variant="danger">{error}</Alert>}
        </>
    );
}

export default function OrderableTable<D extends { id: number }>({ filterables, params: propParams, ...props }: InnerOrderableTableProps<D> & {
    filterables?: Array<{
        field_name: string;
        label: string | React.JSX.Element;
        options: { value: string; label: string }[]
    }>;
}) {
    const { params: queryParams, updateParam } = useQuerySearchParams();

    const onPageChange = useCallback((page: number) => {
        // @ts-ignore
        updateParam({ target: { value: page, name: OrderableTableQueryParameters.page }});
    }, [updateParam]);
    const onPageSizeChange = useCallback((size: number) => {
        // @ts-ignore
        updateParam({ target: { value: size, name: OrderableTableQueryParameters.size }});
    }, [updateParam]);

    const params = useMemo(() => {
        return {
            ...(propParams && typeof propParams === 'object' ? propParams : {}),
            ...(queryParams && typeof queryParams === 'object' ? queryParams : {}),
        }
    }, [propParams, queryParams]);

    return (
        <>
            {filterables && (
                <form className="d-flex my-2 gap-2">
                    {filterables?.map((f) => (
                        <FormGroup key={f.field_name}>
                            <label className="text-sm">{f.label}</label>
                            <select name={f.field_name} className="form-control" onChange={updateParam}>
                                <option value="">----</option>
                                {f.options?.map((o) => (
                                    <option
                                        key={o.value}
                                        selected={f.field_name in params && params[f.field_name] === o.value}
                                        value={o.value}
                                    >
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </FormGroup>
                    ))}
                </form>
            )}
            <PaginatedContextProvider
              initPage={params[OrderableTableQueryParameters.page] ? parseInt(params[OrderableTableQueryParameters.page], 10) : 0}
              initSize={params[OrderableTableQueryParameters.size] ? parseInt(params[OrderableTableQueryParameters.size], 10) : 25}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            >
                <InnerOrderableTable<D> {...props} params={params} />
            </PaginatedContextProvider>
        </>
    )
}
